<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch, type ComponentPublicInstance } from 'vue'

import UiIcon from '@/components/common/UiIcon.vue'
import {
  THEME_COLOR_PROFILE_OPTIONS,
  applyThemeColorProfileOverride,
  getAppliedThemeColorProfile,
  setThemeColorProfileOverride,
  type ThemeColorProfile,
} from '@/theme/colorProfiles'

const rootEl = ref<HTMLElement | null>(null)
const btnEl = ref<HTMLButtonElement | null>(null)
const open = ref(false)
const activeIndex = ref(0)
const optionEls = ref<(HTMLButtonElement | null)[]>([])
const menuId = 'color-profile-picker-menu'
const triggerId = 'color-profile-picker-trigger'
const panelLabel = 'Color theme'
const currentProfile = ref<ThemeColorProfile | null>(null)
let observer: MutationObserver | null = null

function syncState() {
  currentProfile.value = getAppliedThemeColorProfile()
}

function setOptionRef(el: Element | ComponentPublicInstance | null, index: number) {
  optionEls.value[index] = el instanceof HTMLButtonElement ? el : null
}

function focusOption(index: number) {
  activeIndex.value = index
  void nextTick(() => {
    optionEls.value[index]?.focus({ preventScroll: true })
  })
}

function openMenu(index?: number) {
  syncState()
  open.value = true
  const currentIndex = THEME_COLOR_PROFILE_OPTIONS.findIndex((option) => option.value === currentProfile.value)
  activeIndex.value = typeof index === 'number' ? index : Math.max(currentIndex, 0)

  if (typeof index === 'number') {
    focusOption(index)
  }
}

function close(options: { restoreFocus?: boolean } = {}) {
  open.value = false

  if (options.restoreFocus) {
    void nextTick(() => {
      btnEl.value?.focus({ preventScroll: true })
    })
  }
}

function toggle() {
  if (open.value) {
    close()
    return
  }

  openMenu()
}

function selectProfile(profile?: ThemeColorProfile | null) {
  setThemeColorProfileOverride(profile)
  syncState()
  close({ restoreFocus: true })
}

function moveActive(delta: number) {
  const total = THEME_COLOR_PROFILE_OPTIONS.length
  if (!total) return

  const nextIndex = (activeIndex.value + delta + total) % total
  focusOption(nextIndex)
}

function focusBoundary(position: 'start' | 'end') {
  if (!THEME_COLOR_PROFILE_OPTIONS.length) return
  focusOption(position === 'start' ? 0 : THEME_COLOR_PROFILE_OPTIONS.length - 1)
}

function onTriggerKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    openMenu(0)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    openMenu(THEME_COLOR_PROFILE_OPTIONS.length - 1)
  }
}

function onMenuKeydown(event: KeyboardEvent) {
  if (!open.value) return

  if (event.key === 'Escape') {
    event.preventDefault()
    close({ restoreFocus: true })
    return
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    moveActive(1)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    moveActive(-1)
    return
  }

  if (event.key === 'Home') {
    event.preventDefault()
    focusBoundary('start')
    return
  }

  if (event.key === 'End') {
    event.preventDefault()
    focusBoundary('end')
  }
}

function onFocusOut(event: FocusEvent) {
  const nextTarget = event.relatedTarget

  if (nextTarget instanceof Node && rootEl.value?.contains(nextTarget)) {
    return
  }

  close()
}

function onPointerDown(event: PointerEvent) {
  if (!open.value) return

  const target = event.target
  if (target instanceof Node && rootEl.value?.contains(target)) {
    return
  }

  close()
}

watch(open, (isOpen) => {
  if (isOpen) {
    document.addEventListener('pointerdown', onPointerDown, true)
    return
  }

  document.removeEventListener('pointerdown', onPointerDown, true)
})

onMounted(() => {
  applyThemeColorProfileOverride()
  syncState()

  if (typeof document === 'undefined') {
    return
  }

  observer = new MutationObserver(() => {
    syncState()
  })

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-color'],
  })
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onPointerDown, true)
  observer?.disconnect()
})
</script>

<template>
  <div
    ref="rootEl"
    class="relative flex items-center"
    @focusout="onFocusOut"
  >
    <button
      :id="triggerId"
      ref="btnEl"
      type="button"
      class="utility-trigger utility-trigger--icon p-2"
      :aria-controls="open ? menuId : undefined"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="panelLabel"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <UiIcon
        name="Palette"
        class="size-6"
      />
    </button>

    <div
      v-if="open"
      :id="menuId"
      :aria-label="panelLabel"
      :aria-labelledby="triggerId"
      role="menu"
      aria-orientation="vertical"
      tabindex="-1"
      class="panel-ui absolute right-0 top-full z-50 mt-2 w-60 p-1"
      @keydown="onMenuKeydown"
    >
      <button
        v-for="(option, index) in THEME_COLOR_PROFILE_OPTIONS"
        :key="option.value"
        :ref="(el) => setOptionRef(el, index)"
        type="button"
        role="menuitem"
        class="panel-option"
        :class="{ 'bg-accent text-accent-foreground': currentProfile === option.value }"
        @mouseenter="activeIndex = index"
        @focus="activeIndex = index"
        @click="selectProfile(currentProfile === option.value ? null : option.value)"
      >
        <span
          class="size-4 shrink-0 rounded-full border border-border"
          aria-hidden="true"
          :style="{ background: `oklch(76.55% .0587 ${option.hue})` }"
        />
        <span class="flex-1 leading-tight">
          <span class="block text-sm font-medium">{{ option.label }}</span>
          <span class="block text-[11px] text-muted-foreground">{{ option.description }}</span>
        </span>
        <!-- <UiIcon
          v-if="currentProfile === option.value"
          name="Check"
          class="size-3.5 text-muted-foreground"
        /> -->
      </button>
    </div>
  </div>
</template>
